import type { Address } from "@kwp/contracts";
import { z } from "zod";

/**
 * The composer's pure parts: recipient parsing and validation, the formatting markers the toolbar
 * inserts, their conversion to safe HTML, and the reply / forward prefill.
 */
export type Recipient = { name: string | null; email: string };

const Email = z.string().email();

/**
 * Parses "Ann <ann@example.com>, bob@example.com; …". Returns the recipients and every entry that
 * is not an address, so the form can say which one is wrong.
 */
export function parseRecipients(value: string): { recipients: Recipient[]; invalid: string[] } {
  const recipients: Recipient[] = [];
  const invalid: string[] = [];
  for (const raw of value.split(/[,;\n]/)) {
    const part = raw.trim();
    if (part === "") continue;
    const angled = /^(.*?)<([^<>]+)>$/.exec(part);
    const name = angled === null ? null : (angled[1] as string).trim().replace(/^"|"$/g, "");
    const email = (angled === null ? part : (angled[2] as string)).trim();
    if (Email.safeParse(email).success) recipients.push({ name: name === null || name === "" ? null : name, email });
    else invalid.push(part);
  }
  return { recipients, invalid };
}

export function formatRecipient(r: Address): string {
  return r.name === null || r.name === "" ? r.email : `${r.name} <${r.email}>`;
}

export type ComposeMode = "new" | "reply" | "forward";

export interface DraftFields {
  to: string;
  cc: string;
  bcc: string;
  subject: string;
  body: string;
}

export type DraftErrors = Partial<Record<keyof DraftFields, string>>;

export const RECIPIENT_LIMIT = 100;

/** What the composer refuses to send, per field. Forwarding has no cc, bcc or subject. */
export function validateDraft(mode: ComposeMode, d: DraftFields): DraftErrors {
  const errors: DraftErrors = {};
  const fields = mode === "forward" ? (["to"] as const) : (["to", "cc", "bcc"] as const);
  for (const field of fields) {
    const { recipients, invalid } = parseRecipients(d[field]);
    if (invalid.length > 0) errors[field] = `Not an email address: ${invalid.join(", ")}`;
    else if (recipients.length > RECIPIENT_LIMIT) errors[field] = `At most ${RECIPIENT_LIMIT} recipients.`;
  }
  if (errors.to === undefined && parseRecipients(d.to).recipients.length === 0) errors.to = "Add at least one recipient.";
  if (mode !== "forward" && d.body.trim() === "") errors.body = "Write a message.";
  if (d.subject.length > 998) errors.subject = "The subject is too long.";
  return errors;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

function inline(escaped: string): string {
  return escaped
    .replace(/\*\*(?=\S)([\s\S]*?\S)\*\*/g, "<strong>$1</strong>")
    .replace(/(^|[\s(])_(?=\S)([^_]*?\S)_(?=$|[\s).,!?:;])/g, "$1<em>$2</em>")
    .replace(/\bhttps:\/\/[^\s<]+[^\s<.,!?:;)]/g, (url) => `<a href="${url}">${url}</a>`);
}

/**
 * The plain-text body as HTML: everything escaped first, then only our own markup — `**bold**`,
 * `_italic_`, lines starting "- " as a list, https links, paragraphs and line breaks. Nothing the
 * author typed can become a tag or an attribute.
 */
export function textToHtml(text: string): string {
  const blocks = text.replace(/\r\n/g, "\n").split(/\n{2,}/);
  return blocks
    .map((block) => {
      const lines = block.split("\n");
      if (lines.every((l) => /^\s*- /.test(l))) {
        return `<ul>${lines.map((l) => `<li>${inline(escapeHtml(l.replace(/^\s*- /, "")))}</li>`).join("")}</ul>`;
      }
      return `<p>${lines.map((l) => inline(escapeHtml(l))).join("<br>")}</p>`;
    })
    .filter((b) => b !== "<p></p>")
    .join("");
}

/** The text part sent next to the HTML: the same words without the bold markers. */
export function plainText(text: string): string {
  return text.replace(/\*\*(?=\S)([\s\S]*?\S)\*\*/g, "$1");
}

export type Format = "bold" | "italic" | "list";

/** Applies a toolbar format to the textarea's selection; returns the new value and selection. */
export function applyFormat(value: string, start: number, end: number, format: Format): { value: string; start: number; end: number } {
  const selected = value.slice(start, end);
  if (format === "list") {
    const lineStart = value.lastIndexOf("\n", start - 1) + 1;
    const block = value.slice(lineStart, end);
    const listed = block
      .split("\n")
      .map((l) => (l.startsWith("- ") ? l : `- ${l}`))
      .join("\n");
    return { value: value.slice(0, lineStart) + listed + value.slice(end), start: lineStart, end: lineStart + listed.length };
  }
  const mark = format === "bold" ? "**" : "_";
  const text = selected === "" ? (format === "bold" ? "bold" : "italic") : selected;
  const next = value.slice(0, start) + mark + text + mark + value.slice(end);
  return { value: next, start: start + mark.length, end: start + mark.length + text.length };
}

const prefixed = (prefix: string, subject: string) => (new RegExp(`^${prefix}:`, "i").test(subject.trim()) ? subject.trim() : `${prefix}: ${subject.trim()}`);

export function replySubject(subject: string): string {
  return prefixed("Re", subject);
}

/**
 * Who a reply goes to: the sender of the message replied to, or, when that was the mailbox
 * itself, the people it was sent to.
 */
export function replyRecipients(message: { from: Address; to: readonly Address[] }, ownAddress: string): Address[] {
  const own = ownAddress.toLowerCase();
  if (message.from.email.toLowerCase() !== own) return [message.from];
  return message.to.filter((a) => a.email.toLowerCase() !== own);
}
